import FormHeader from "@/components/booking/FormHeader.tsx";
import {FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form.tsx";
import {Input} from "@/components/ui/input.tsx";
import {useFormContext} from "react-hook-form";
import {z} from "zod";
import {BookingFormSchema} from "@/components/booking/form/BookingFormSchema.ts";
import {APIProvider, useMapsLibrary} from "@vis.gl/react-google-maps";
import React, {useContext, useEffect, useRef, useState} from "react";
import {AddressChecker, AddressCheckState} from "@/components/booking/form/AddressChecker.tsx";
import {Textarea} from "@/components/ui/textarea.tsx";
import Api from "@/lib/oscar/Api.ts";
import {OscarContext, OscarContextType} from "@/components/context/OscarContext.tsx";
import {formatCents} from "@/lib/utils.ts";
import {BookingKind, BookingKindHelper} from "@/lib/enum/BookingKind.ts";

export function AddressForm() {
    const { authToken } = useContext(OscarContext) as OscarContextType
    const {control, setValue, resetField, watch} = useFormContext<z.infer<typeof BookingFormSchema>>();
    const [selected, setSelected] = useState<AddressState | null>(null);
    const [checkState, setCheckState] = useState<AddressCheckState | null>(null);
    const binKind = watch("binKind");

    /// When a place is selected, check if it is in the service area.
    const onPlaceSelect = (place: google.maps.places.PlaceResult | null) => {
        if (place && place.geometry?.location) {
            let subpremise = "";
            let streetNumber = "";
            let address1 = "";
            let city = "";
            let state = "";
            let postcode = "";

            for (const component of place.address_components ?? []) {
                const componentType = component.types[0];
                switch (componentType) {
                    case "street_number": {
                        streetNumber += component.short_name;
                        break;
                    }
                    case "subpremise": {
                        subpremise += component.long_name;
                        break;
                    }
                    case "route": {
                        address1 += component.short_name;
                        break;
                    }
                    case "locality":
                        city = component.long_name;
                        break;
                    case "administrative_area_level_1": {
                        state = component.short_name;
                        break;
                    }
                    case "postal_code": {
                        postcode = component.short_name;
                        break;
                    }
                }
            }

            if (streetNumber != "") {
                address1 = `${streetNumber} ${address1}`;
            }

            if (subpremise != "") {
                address1 = `${subpremise}/${address1}`;
            }

            if (address1 == "" || city == "" || state == "" || postcode == "") {
                setSelected(null);
                setCheckState({ error: "Please select a valid address." });
                return
            } else if (streetNumber == "") {
                setSelected(null);
                setCheckState({ error: "Please select an address with a street number." });
                return
            } else {
                setSelected({
                    address1: address1,
                    suburb: city,
                    state: state,
                    postcode: postcode,
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                });
            }
        }
    };


    useEffect(() => {
        const checkServiceArea = async (selected: AddressState) => {
            setCheckState({ isChecking: true });

            try {
                const result = await Api.checkLocation(authToken, selected.lat, selected.lng);

                if (!result.valid) {
                    setCheckState({ error: "We don't current serve the address you entered, please call us for more options." });
                    setSelected(null);
                    // Set the form.
                    fillForm(null, null);
                } else {
                    // Set the additional charge.
                    let additionalCharge: string|null = null;
                    if (result.cost != null && result.cost > 0) {
                        additionalCharge = formatCents(result.cost);
                    }

                    setCheckState({ isServiced: true, additionalCharge: additionalCharge });
                    // Set the form.
                    fillForm(selected, result.cost);
                }
            } catch (error) {
                console.error('Error checking service area:', error);
                setCheckState({ error: "An error occurred while validating your address. Please try again later or call for a booking." });
                setSelected(null);
            }
        };

        /// Fill the form with the selected address.
        const fillForm = (selected: AddressState | null, cost: number | null) => {
            if (selected) {
                setValue("address", selected.address1);
                setValue("suburb", selected.suburb);
                setValue("state", selected.state);
                setValue("postcode", selected.postcode);
                setValue("lat", selected.lat);
                setValue("lng", selected.lng);
                setValue("distanceCharge", cost);
            } else {
                resetField("address");
                resetField("suburb");
                resetField("state");
                resetField("postcode");
                resetField("lat");
                resetField("lng");
                resetField("distanceCharge");
            }
        }

        if (selected !== null) {
            checkServiceArea(selected).then(() => {});
        }
    }, [selected, setValue, resetField, authToken]);

    return (
        <div className={"os-app-row os-flex os-flex-col os-gap-4"}>
            <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
                <div className={"os-flex os-flex-col os-gap-4"}>
                    <FormHeader>Where would you like it dropped off to?</FormHeader>
                    <FormItem>
                        <FormLabel>Address Search</FormLabel>
                        <FormControl>
                            <AddressInput onPlaceSelect={onPlaceSelect}/>
                        </FormControl>
                        <FormDescription>
                            If you can't find your address please call us to place a booking.
                        </FormDescription>
                    </FormItem>
                </div>
            </APIProvider>

            {(checkState) && (
                <AddressChecker state={checkState}/>
            )}

            <div>
                <FormLabel>Selected Address</FormLabel>
                { watch('address') && watch('suburb') && watch('state') && watch('postcode') && (
                    <div className={"os-font-bold"}>
                        {watch('address')}, {watch('suburb')}, {watch('state')} {watch('postcode')}
                    </div>
                )}
                { (!watch('address') || !watch('suburb') || !watch('state') || !watch('postcode')) && (
                    <div className={"os-font-bold os-text-red-700"}>
                        Please find your address.
                    </div>
                )}
            </div>

            <FormField
                control={control}
                name="notes"
                render={({field}) => (
                    <FormItem>
                        <FormLabel>Special Requests, {binKind == BookingKind.BAG ? "Waste Bag" : "Skip Bin"} Placement (Where would you like the {BookingKindHelper.getBookingKindSlug(binKind)} placed), Additional Information</FormLabel>
                        <FormControl>
                            <Textarea
                                required={true}
                                placeholder={(binKind == BookingKind.BAG) ? "Is there anything extra we need to know about your drop off or pickup." : "Do you need a drop end bin (not available for all bin sizes and subject to availability) and is there anything extra we need to know about your drop off or pickup?"}
                                className="resize-none"
                                {...field}
                            />
                        </FormControl>
                        <FormDescription>
                            We will contact you if we require more information.
                        </FormDescription>
                        <FormMessage/>
                    </FormItem>
                )}
            />
        </div>
    );
}

interface PlaceAutocompleteProps {
    onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
}

const AddressInput = ({ onPlaceSelect }: PlaceAutocompleteProps) => {
    const [placeAutocomplete, setPlaceAutocomplete] =
        useState<google.maps.places.Autocomplete | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const places = useMapsLibrary('places');

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    }

    useEffect(() => {
        if (!places || !inputRef.current) return;

        const options = {
            componentRestrictions: { country: "au" },
            fields: ["address_components", "place_id", "geometry"],
            types: ["address"],
            strictBounds: false,
            bounds: new google.maps.LatLngBounds(
                new google.maps.LatLng(-36.575114999005145, 148.15000281187426),
                new google.maps.LatLng(-34.67159375911924, 149.80437186812387)
            ),
        };

        setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
    }, [places]);

    useEffect(() => {
        if (!placeAutocomplete) return;

        placeAutocomplete.addListener('place_changed', () => {
            onPlaceSelect(placeAutocomplete.getPlace());
        });
    }, [placeAutocomplete, onPlaceSelect]);


    return (
        <div className="autocomplete-container">
            <Input autoComplete={"address-line1"} required={true} ref={inputRef} onKeyDown={onKeyDown} />
        </div>
    );
};

type AddressState = {
    address1: string;
    suburb: string;
    state: string;
    postcode: string;
    lat: number;
    lng: number;
}
